import type {
  InferContractRouterInputs,
  InferContractRouterOutputs,
} from '@orpc/contract'
import {
  type DefaultError,
  useMutation,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query'
import { useAtomValue, useSetAtom } from 'jotai'
import { atomWithImmer } from 'jotai-immer'
import type { BaseContract } from 'pixai-contract/api/orpc'
import { useEffect } from 'react'
import { useMyInfo } from '../apollo/auth'
import { contractClient, orpc } from './contract'

type KaisuukenContract = InferContractRouterOutputs<BaseContract>['kaisuuken']
export type KaisuukenCheckInput =
  InferContractRouterInputs<BaseContract>['kaisuuken']['check']

/** Stable, non-hook function for calling the kaisuuken check API. */
export function executeKaisuukenCheck(input: KaisuukenCheckInput) {
  return contractClient.kaisuuken.check(input)
}

export const useKaisuukenCheckMatching = () => {
  return useMutation({
    mutationFn: executeKaisuukenCheck,
  })
}

export function useKaisuukenLogs(params: {
  before?: string
  after?: string
  first?: number
  last?: number
}) {
  const { data: me } = useMyInfo()
  return useQuery({
    ...orpc.kaisuuken.logs.queryOptions({ input: params }),
    enabled: Boolean(me),
    // User-scoped log feed — drop cache across login/logout so a returning
    // user doesn't briefly see the previous account's entries.
    meta: { dependsOn: ['login'] },
  })
}

export const useKaisuukenSummary = () => {
  const { data: me } = useMyInfo()
  return useQuery({
    ...orpc.kaisuuken.summary.queryOptions(),
    enabled: Boolean(me),
    // Summary lists the current user's cards — must invalidate across
    // login/logout.
    meta: { dependsOn: ['login'] },
  })
}

type KaisuukenTemplateListResult = KaisuukenContract['templateList']

export type KaisuukenTemplate = KaisuukenTemplateListResult['data'][number]

const kaisuukenTemplateCacheAtom = atomWithImmer(
  new Map<string, KaisuukenTemplate>(),
)

/**
 * Hook to get a Kaisuuken template by its ID from the cache
 */
export function useKaisuukenTemplate(templateId?: string) {
  const cache = useAtomValue(kaisuukenTemplateCacheAtom)
  return templateId ? cache.get(templateId) : undefined
}

/**
 * Hook to fetch Kaisuuken templates by their IDs
 */
export function useListKaisuukenTemplates(
  vars?: {
    templateIds?: string[]
  },
  opts?: UseQueryOptions<
    KaisuukenTemplateListResult,
    DefaultError,
    KaisuukenTemplateListResult['data']
  >,
) {
  const queryRes = useQuery({
    ...orpc.kaisuuken.templateList.queryOptions({
      input: { ids: vars?.templateIds ?? [] },
    }),
    select: ({ data }) => data,
    enabled: Boolean(vars?.templateIds?.length),
    ...opts,
  })

  const setCache = useSetAtom(kaisuukenTemplateCacheAtom)

  useEffect(() => {
    setCache(cache => {
      for (const it of queryRes.data ?? []) {
        cache.set(it.templateId, it)
      }
    })
  }, [queryRes.data, setCache])

  return queryRes
}
